/**
 * config.js — PFS Get Verified: single source of truth
 * ------------------------------------------------------
 * Every "business rule" for the verification flow lives in this file.
 * app.js never hard-codes a state, tier, or field — it just reads these
 * objects and renders/validates from them.
 *
 * TO EXPAND THE SYSTEM LATER:
 *   - Add/remove a state PFS sells in        -> edit TERRITORY_STATES
 *   - Add/remove a membership tier            -> edit TIERS
 *   - Change which tiers a business type sees -> edit BUSINESS_TYPES[x].tiers
 *   - Add/remove a question on the form       -> edit FORM_SECTIONS
 * You should never need to touch app.js or index.html for those changes.
 */

// -----------------------------------------------------------------------
// 1. TERRITORY
// -----------------------------------------------------------------------
// Anyone whose state isn't in this list gets redirected to Chemmax instead
// of seeing the application. Add a state here the day PFS starts shipping
// there — nothing else in the codebase needs to change.
const TERRITORY_STATES = [
  { code: "CA", name: "California" },
  { code: "AZ", name: "Arizona" },
  { code: "NV", name: "Nevada" },
  { code: "OR", name: "Oregon" },
  { code: "WA", name: "Washington" },
  { code: "ID", name: "Idaho" },
  { code: "AK", name: "Alaska" },
  { code: "HI", name: "Hawaii" },
  { code: "UT", name: "Utah" },
];

// Where out-of-territory applicants get sent instead of applying.
const OUT_OF_TERRITORY_REDIRECT_URL = "https://chemmax.com/";

// -----------------------------------------------------------------------
// 2. MEMBERSHIP TIERS
// -----------------------------------------------------------------------
// "appliesTo" lists which business-type answers can be offered this tier.
// The values must match keys in BUSINESS_TYPES below.
const TIERS = {
  verified: {
    name: "PFS Verified",
    price: "Free",
    blurb: "Verified professional account with standard PFS pricing and qualified product access.",
    appliesTo: ["carpet", "both"],
  },
  advantage: {
    name: "PFS Advantage",
    price: "Paid",
    blurb: "Preferred member pricing and additional purchasing advantages.",
    appliesTo: ["carpet", "both"],
  },
  elite: {
    name: "PFS Elite",
    price: "Rug Facility",
    blurb: "Verified rug facilities only. Complete qualified catalog access, including ChemMax Rug Systems.",
    appliesTo: ["rug", "both"],
  },
};

// -----------------------------------------------------------------------
// 3. BUSINESS TYPES
// -----------------------------------------------------------------------
// "sections" lists which FORM_SECTIONS (below) get shown for this answer.
// "tiers" lists which TIERS (above) get displayed as the applicant's
// likely outcome — final tier assignment always happens at manual review,
// this is informational only.
const BUSINESS_TYPES = {
  carpet: {
    label: "Carpet / Upholstery Cleaner",
    description: "Professional carpet, upholstery, tile, or low-moisture cleaning.",
    tiers: ["verified", "advantage"],
    sections: ["carpet_business"],
  },
  rug: {
    label: "Rug Cleaner / Rug Facility",
    description: "Dedicated in-plant area rug cleaning facility.",
    tiers: ["elite"],
    sections: ["rug_facility"],
  },
  both: {
    label: "Both",
    description: "Operate a carpet/upholstery business and a separate rug cleaning facility.",
    tiers: ["elite"],
    sections: ["carpet_business", "rug_facility"],
  },
};

// -----------------------------------------------------------------------
// 4. FORM SECTIONS & FIELDS
// -----------------------------------------------------------------------
// Each section is a named group of fields. A business-type answer pulls
// in one or more sections (see BUSINESS_TYPES[x].sections above).
//
// field.type options the renderer understands: "text", "tel", "email",
// "url", "textarea", "file"
//
// Adding a field: add one object to the array. The renderer, validator,
// and payload-builder in app.js all read this list generically — no
// per-field code needed anywhere else.
const FORM_SECTIONS = {
  carpet_business: {
    title: "Carpet / Upholstery Business",
    fields: [
      { id: "carpet_business_name", label: "Business legal name", type: "text", required: true },
      { id: "carpet_business_address", label: "Business address", type: "text", required: true },
      { id: "carpet_shipping_address", label: "Shipping address (if different)", type: "text", required: false },
      { id: "carpet_phone", label: "Business phone", type: "tel", required: true },
      { id: "carpet_email", label: "Business email", type: "email", required: true },
      { id: "carpet_website", label: "Website", type: "url", required: false },
      { id: "carpet_years_operating", label: "Years in operation", type: "text", required: true },
      { id: "carpet_review_link", label: "Google or Yelp review link", type: "url", required: true },
      { id: "carpet_proof_of_ownership", label: "Proof of ownership (business license, EIN letter, or insurance cert)", type: "file", required: true },
      { id: "carpet_business_photos", label: "Photos of your van, equipment, facility, and/or team", type: "file", required: true, multiple: true },
    ],
  },
  rug_facility: {
    title: "Rug Cleaning Facility",
    fields: [
      { id: "rug_business_name", label: "Facility legal business name", type: "text", required: true },
      { id: "rug_facility_address", label: "Facility physical address", type: "text", required: true },
      { id: "rug_shipping_address", label: "Shipping address (if different)", type: "text", required: false },
      { id: "rug_phone", label: "Facility phone", type: "tel", required: true },
      { id: "rug_email", label: "Facility email", type: "email", required: true },
      { id: "rug_website", label: "Website", type: "url", required: false },
      { id: "rug_years_operating", label: "Years in operation", type: "text", required: true },
      { id: "rug_review_link", label: "Google or Yelp review link", type: "url", required: true },
      { id: "rug_proof_of_ownership", label: "Proof of ownership (business license, EIN letter, or insurance cert)", type: "file", required: true },
      { id: "rug_facility_photos", label: "Photos of your facility, equipment, van, and team", type: "file", required: true, multiple: true },
    ],
  },
};

// Exposed as a single namespaced object so nothing else needs multiple
// import lines, and so a future move to real ES modules is a one-line change.
window.PFS_CONFIG = {
  TERRITORY_STATES,
  OUT_OF_TERRITORY_REDIRECT_URL,
  TIERS,
  BUSINESS_TYPES,
  FORM_SECTIONS,
};